import { useNavigate } from 'react-router-dom'

/**
 * Forbidden Access Page
 * 
 * Displayed when a user tries to access a route they don't have permission for.
 * Provides clear feedback and navigation options.
 * 
 * @param {Object} adminData - Current user data
 * @param {Array<string>} allowedRoles - Roles that are allowed to access the page
 */
const ForbiddenPage = ({ adminData, allowedRoles = [] }) => {
  const navigate = useNavigate()

  const roleLabels = {
    admin: 'Administrateur',
    editor: 'Éditeur',
    reader: 'Lecteur (Consultant)'
  }

  const getAccessiblePages = (role) => {
    const pages = [
      { name: 'Tableau de bord', path: '/admin/dashboard', roles: ['admin', 'editor', 'reader'] },
      { name: 'Évaluations', path: '/admin/assessments', roles: ['admin', 'editor', 'reader'] },
      { name: 'Questionnaire', path: '/admin/questionnaire', roles: ['admin', 'editor'] },
      { name: 'Utilisateurs', path: '/admin/users', roles: ['admin'] },
      { name: 'Tests & Utilitaires', path: '/admin/tests', roles: ['admin'] }
    ]

    return pages.filter(page => page.roles.includes(role))
  }

  const accessiblePages = getAccessiblePages(adminData?.role)
  const allowedRoleLabels = allowedRoles.map(role => roleLabels[role] || role).join(', ')

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 text-center mb-4">
          Accès refusé
        </h1>

        {/* Description */}
        <div className="text-center mb-6">
          <p className="text-lg text-gray-700 mb-2">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
          <p className="text-sm text-gray-600">
            Cette page est réservée aux utilisateurs avec le rôle : <span className="font-semibold">{allowedRoleLabels}</span>
          </p>
        </div>

        {/* Current User Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-blue-900">Votre compte actuel</p>
              <p className="text-sm text-blue-700 mt-1">
                <span className="font-medium">{adminData?.email}</span>
              </p>
              <p className="text-sm text-blue-700">
                Rôle : <span className="font-medium">{roleLabels[adminData?.role] || adminData?.role}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Accessible Pages */}
        {accessiblePages.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Pages auxquelles vous avez accès :
            </h3>
            <div className="space-y-2">
              {accessiblePages.map((page) => (
                <button
                  key={page.path}
                  onClick={() => navigate(page.path)}
                  className="w-full text-left px-4 py-2 bg-white border border-gray-200 rounded-md hover:bg-blue-50 hover:border-blue-300 transition-colors text-sm text-gray-700 hover:text-blue-700"
                >
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    {page.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Retour au tableau de bord
          </button>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Page précédente
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Si vous pensez que c'est une erreur, contactez votre administrateur système.
          </p>
        </div>
      </div>
    </div>
  )
}

export default ForbiddenPage

